import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FaTimes, FaPlus, FaTrash, FaBuilding } from 'react-icons/fa';
import { getApiEndpoint } from '../../../../config/api';
import { getToken } from '../../../../utils/auth';

function ManagementSettings() {
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [managementList, setManagementList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const buildHeaders = useCallback(() => {
    const t = getToken();
    return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  }, []);

  const fetchProperties = useCallback(async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/properties'), { headers: buildHeaders() });
      if (res.ok) {
        const data = await res.json();
        setProperties(data);
        setSelectedPropertyId(prev => (!prev && data.length > 0) ? data[0].id : prev);
      }
    } catch (e) {
      console.error('Failed to fetch properties:', e);
    }
  }, [buildHeaders]);

  const fetchManagement = useCallback(async () => {
    if (!selectedPropertyId) return;
    setLoading(true);
    try {
      const res = await fetch(
        getApiEndpoint(`/api/management/list?property_id=${selectedPropertyId}`),
        { headers: buildHeaders() }
      );
      if (res.ok) setManagementList(await res.json());
    } catch (e) {
      console.error('Failed to fetch management companies:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedPropertyId, buildHeaders]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);
  useEffect(() => { fetchManagement(); }, [fetchManagement]);

  const handleRemove = async (linkId, companyName) => {
    if (!window.confirm(`Remove ${companyName} from this property?`)) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/management/${linkId}`), {
        method: 'DELETE',
        headers: buildHeaders()
      });
      if (res.ok) {
        toast.success('Management company removed');
        fetchManagement();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.message || 'Failed to remove', { autoClose: 4000 });
      }
    } catch (e) {
      toast.error('Network error', { autoClose: 4000 });
    }
  };

  return (
    <div className="stg-max-wide">
      <div className="stg-page-header">
        <h1 className="stg-page-title">Management Company</h1>
        <p className="stg-page-subtitle">
          Add a facility management company to your properties using their 6-digit CloudSafe ID.
          Once added, they can manage inspections, compliance and maintenance on your behalf.
        </p>
      </div>

      <div className="stg-form-group">
        <label className="stg-form-label">Property</label>
        <select
          className="stg-select"
          style={{ maxWidth: 360 }}
          value={selectedPropertyId}
          onChange={e => setSelectedPropertyId(Number(e.target.value))}
        >
          {properties.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      <div className="stg-section-header">
        <h2 className="stg-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Assigned Management {managementList.length > 0 && <span className="stg-count">{managementList.length}</span>}
        </h2>
        <button className="stg-btn stg-btn-primary stg-btn-sm" onClick={() => setShowAddModal(true)} disabled={!selectedPropertyId}>
          <FaPlus /> Add Company
        </button>
      </div>

      {loading ? (
        <div className="stg-loading">Loading...</div>
      ) : managementList.length === 0 ? (
        <div className="stg-empty">
          <FaBuilding className="stg-empty-icon" />
          <p>No management company assigned to this property yet.</p>
          <button className="stg-btn stg-btn-outline stg-btn-sm" onClick={() => setShowAddModal(true)}>
            <FaPlus /> Add Management Company
          </button>
        </div>
      ) : (
        <div className="stg-entity-list">
          {managementList.map(m => (
            <div className="stg-entity-card" key={m.link_id}>
              <div className="stg-entity-info">
                <div className="stg-entity-avatar"><FaBuilding /></div>
                <div>
                  <h3 className="stg-entity-name">{m.company_name || `${m.first_name} ${m.last_name}`}</h3>
                  <p className="stg-entity-detail">{m.email}</p>
                  {m.phone_number && <p className="stg-entity-detail">{m.phone_number}</p>}
                  <p className="stg-entity-code">CloudSafe ID: {m.company_code}</p>
                </div>
              </div>
              <button
                className="stg-remove-btn"
                onClick={() => handleRemove(m.link_id, m.company_name || m.first_name)}
                title="Remove"
              >
                <FaTrash />
              </button>
            </div>
          ))}
        </div>
      )}

      {showAddModal && selectedPropertyId && (
        <AddManagementModal
          selectedPropertyId={selectedPropertyId}
          propertyName={properties.find(p => p.id === selectedPropertyId)?.name || ''}
          onClose={() => setShowAddModal(false)}
          onAdded={fetchManagement}
        />
      )}
    </div>
  );
}

function AddManagementModal({ selectedPropertyId, propertyName, onClose, onAdded }) {
  const [companyCode, setCompanyCode] = useState('');
  const [step, setStep] = useState('enter');
  const [verified, setVerified] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const doAdd = async (code) => {
    const authToken = getToken();
    const res = await fetch(getApiEndpoint('/api/management/add'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
      },
      body: JSON.stringify({ company_code: code, property_id: selectedPropertyId })
    });
    return { res, data: await res.json().catch(() => ({})) };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = companyCode.replace(/\D/g, '').slice(0, 6);
    if (code.length !== 6) {
      setError('CloudSafe Company ID must be exactly 6 digits');
      return;
    }
    const authToken = getToken();
    if (!authToken) {
      setError('You must be logged in. Please log in again.');
      return;
    }
    setError('');
    setVerified(null);
    setSubmitting(true);

    try {
      const lookupRes = await fetch(getApiEndpoint(`/api/management/lookup?company_code=${encodeURIComponent(code)}`), {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` }
      }).catch(() => null);

      if (lookupRes && lookupRes.ok) {
        const lookupData = await lookupRes.json().catch(() => ({}));
        if (lookupData.found) {
          setVerified({ company_name: lookupData.company_name, company_code: lookupData.company_code });
          setStep('verify');
          setSubmitting(false);
          return;
        }
        if (!lookupData.found) {
          setError(lookupData.message || 'No management company found with this CloudSafe ID');
          setSubmitting(false);
          return;
        }
      }

      const { res, data } = await doAdd(code);
      if (!res.ok) {
        setError(data.message || 'No management company found with this CloudSafe ID');
        setSubmitting(false);
        return;
      }
      toast.success(`${data.company_name || 'Management company'} added. They can now view and manage this property.`);
      onAdded();
      onClose();
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmAdd = async (e) => {
    e.preventDefault();
    if (!verified) return;
    setSubmitting(true);
    setError('');
    try {
      const { res, data } = await doAdd(verified.company_code);
      if (!res.ok) {
        setError(data.message || 'Failed to add management company');
        setSubmitting(false);
        return;
      }
      toast.success(`${data.company_name || verified.company_name || 'Management company'} added. They can now view and manage this property.`);
      onAdded();
      onClose();
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="stg-modal-overlay" onClick={onClose}>
      <div className="stg-modal" onClick={e => e.stopPropagation()}>
        <div className="stg-modal-header">
          <h3>{step === 'verify' ? 'Confirm management company' : 'Add Management Company'}</h3>
          <button type="button" className="stg-modal-close" onClick={onClose}><FaTimes /></button>
        </div>
        {step === 'enter' ? (
          <form onSubmit={handleSubmit}>
            <div className="stg-modal-body">
              <p className="stg-modal-hint">
                Enter the management company's 6-digit CloudSafe ID. Once added, they will have access
                to manage this property's inspections, compliance and maintenance.
              </p>
              <div className="stg-form-group">
                <label className="stg-form-label">Property</label>
                <div className="stg-readonly-field">{propertyName}</div>
              </div>
              <div className="stg-form-group">
                <label className="stg-form-label">CloudSafe Company ID <span className="stg-required">*</span></label>
                <input
                  type="text"
                  className="stg-input"
                  placeholder="e.g. 123456"
                  value={companyCode}
                  onChange={e => setCompanyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  autoComplete="off"
                  autoFocus
                />
              </div>
              {error && <p className="stg-error-text">{error}</p>}
            </div>
            <div className="stg-modal-footer">
              <button type="button" className="stg-btn stg-btn-outline stg-btn-sm" onClick={onClose}>Cancel</button>
              <button type="submit" className="stg-btn stg-btn-primary stg-btn-sm" disabled={submitting}>
                {submitting ? 'Looking up\u2026' : 'Add Company'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleConfirmAdd}>
            <div className="stg-modal-body">
              <p className="stg-modal-hint">
                Please verify this is the correct company before granting access to your property.
              </p>
              <div className="stg-form-group">
                <label className="stg-form-label">Property</label>
                <div className="stg-readonly-field">{propertyName}</div>
              </div>
              <div className="stg-verify-box">
                <p className="stg-verify-label">Company name</p>
                <p className="stg-verify-value">{verified?.company_name}</p>
                <p className="stg-verify-label">CloudSafe Company ID</p>
                <p className="stg-verify-value stg-verify-code">{verified?.company_code}</p>
              </div>
              {error && <p className="stg-error-text">{error}</p>}
            </div>
            <div className="stg-modal-footer">
              <button type="button" className="stg-btn stg-btn-outline stg-btn-sm" onClick={() => { setStep('enter'); setVerified(null); setError(''); }}>Back</button>
              <button type="submit" className="stg-btn stg-btn-primary stg-btn-sm" disabled={submitting}>
                {submitting ? 'Adding\u2026' : 'Confirm & grant access'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ManagementSettings;
